import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';

import { logout } from '../../api/auth';
import { useSignedInEmail } from '../../lib/signedInAccount';
import { useSession } from '../../session';

/**
 * The top-bar account control (walkthrough F-8, F-9, F-38): who is signed in, the one Settings
 * entry point, and Sign out.
 */
export function AccountMenu() {
  const { session, setSession } = useSession();
  const [location, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const email = useSignedInEmail(session);

  useEffect(() => setOpen(false), [location]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      // Only an Escape pressed inside the menu closes it and returns focus to its trigger.
      if (event.key === 'Escape' && rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!session) return null;
  const initial = (email || 'A').charAt(0).toUpperCase();

  async function signOut() {
    setError('');
    setBusy(true);
    try {
      await logout();
      setSession(null);
      navigate('/login', { replace: true });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Sign out could not be completed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="account-menu"
      ref={rootRef}
      onBlur={(event) => {
        // Focus moving out of the menu (e.g. tabbing into the page) dismisses it.
        const next = event.relatedTarget as Node | null;
        if (next && !rootRef.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        type="button"
        className="account-menu-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={email ? `Account: signed in as ${email}` : 'Account: signed in'}
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="account-avatar" aria-hidden="true">{initial}</span>
        <span className="account-menu-label">{email || 'Account'}</span>
      </button>
      <div className="account-menu-panel" id={panelId} hidden={!open}>
        <p className="account-menu-identity">
          <span className="account-menu-caption">Signed in as</span>
          {email ? (
            <strong className="account-menu-email">{email}</strong>
          ) : (
            <span className="account-menu-email-unknown">
              this account. Its email shows here after you next sign in on this device.
            </span>
          )}
        </p>
        <Link to="/projects" className="account-menu-item" onClick={() => setOpen(false)}>Back to your studio</Link>
        <Link to="/settings" className="account-menu-item" onClick={() => setOpen(false)}>Settings</Link>
        <Link to="/connect" className="account-menu-item" onClick={() => setOpen(false)}>Connect your AI tool</Link>
        {error ? <p className="error" role="alert">{error}</p> : null}
        <button type="button" className="account-menu-item account-menu-signout" disabled={busy} onClick={() => void signOut()}>
          {busy ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}
